import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Send } from "lucide-react";
import { initials } from "@/mobile/mobileUtils";
import { toast } from "sonner";

export default function MobileChatThread() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const [sp] = useSearchParams();
  const focusMsg = sp.get("m");
  const flashed = useRef(null);

  const load = () => api.get(`/team/channels/${cid}/messages`, { params: { include: focusMsg || undefined } }).then((r) => setMsgs(r.data || [])).catch(() => {});
  useEffect(() => {
    api.get("/team/channels").then((r) => {
      const c = (r.data || []).find((x) => (x.id || x._id) === cid);
      setTitle(c ? c.name : "Conversation");
    }).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [cid]);

  useEffect(() => {
    if (focusMsg && flashed.current !== focusMsg) {
      const el = document.querySelector(`[data-msg-id="${focusMsg}"]`);
      if (!el) return;
      flashed.current = focusMsg;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("target-flash"); setTimeout(() => el.classList.remove("target-flash"), 2700);
      return;
    }
    if (!focusMsg) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, focusMsg]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try { await api.post(`/team/channels/${cid}/messages`, { body }); setText(""); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]" data-testid="mobile-chat-thread">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-3 h-12 flex items-center gap-2">
        <button onClick={() => nav("/m/chat")} data-testid="chat-back"><ArrowLeft className="h-5 w-5 text-slate-500" /></button>
        <div className="font-head font-bold text-slate-900 truncate">{title}</div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-slate-50">
        {msgs.length === 0 && <div className="text-center text-sm text-slate-400 py-10">No messages yet. Say hello 👋</div>}
        {msgs.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid="chat-message" data-msg-id={m.id}>
              {!mine && <div className="h-7 w-7 rounded-full bg-secondary text-white text-[9px] font-bold flex items-center justify-center shrink-0 mt-1">{initials(m.sender_name)}</div>}
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${mine ? "bg-primary text-white rounded-br-sm" : "bg-white border border-slate-100 text-slate-800 rounded-bl-sm"}`}>
                {!mine && <div className="text-[10px] font-semibold text-primary mb-0.5">{m.sender_name}</div>}
                <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                {(m.client_name || m.app_number) && <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-400"}`}>🔗 {m.app_number || m.client_name}</div>}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="border-t border-slate-100 bg-white p-2.5 flex items-end gap-2" style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}>
        <textarea data-testid="chat-input" rows={1} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Message" className="flex-1 resize-none max-h-28 px-3.5 py-2.5 rounded-2xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
        <button onClick={send} disabled={sending || !text.trim()} data-testid="chat-send" className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-40 shrink-0"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
